import React, { useState, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { usePrivacy } from '../contexts/PrivacyContext';
import TimeFilter, { TimeFilterOption } from './TimeFilter';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface PerformanceChartProps {
  data: Array<{
    date: string;
    total_value_eur: number;
    total_investment_eur?: number;
    real_growth_eur?: number;
    real_growth_percentage?: number;
    methodology?: string;
  }>;
}

const PerformanceChart: React.FC<PerformanceChartProps> = ({ data }) => {
  const [selectedFilter, setSelectedFilter] = useState<TimeFilterOption>('MAX');
  
  // Verificar se estamos no dashboard público
  const isPublicDashboard = window.location.pathname.includes('/public/');
  
  // Sempre chamar o hook do contexto de privacidade
  const { valuesHidden } = usePrivacy();
  
  // No dashboard público, sempre ocultar valores
  const shouldHideValues = isPublicDashboard || valuesHidden;
  
  // Filtrar dados baseado no filtro selecionado
  const filteredData = useMemo(() => {
    if (selectedFilter === 'MAX') {
      return data;
    }
    
    const now = new Date();
    let daysBack = 0;
    
    switch (selectedFilter) {
      case '30D':
        daysBack = 30;
        break;
      case '60D':
        daysBack = 60;
        break;
      case '90D':
        daysBack = 90;
        break;
      case '180D':
        daysBack = 180;
        break;
      case '1Y':
        daysBack = 365;
        break;
      case '2Y':
        daysBack = 730;
        break;
      case '5Y':
        daysBack = 1825;
        break;
    }
    
    const cutoffDate = new Date(now.getTime() - (daysBack * 24 * 60 * 60 * 1000));
    return data.filter(item => new Date(item.date) >= cutoffDate);
  }, [data, selectedFilter]);
  
  // Mostrar apenas as últimas 10 leituras quando o filtro MAX está ativo
  const limitedData = selectedFilter === 'MAX' ? filteredData.slice(-10) : filteredData;
  
  // Verificar se temos dados de crescimento real (TWR Enhanced)
  const hasRealGrowthData = limitedData.length > 0 && limitedData[0].real_growth_percentage !== undefined;
  
  // Usar dados de crescimento real se disponíveis, senão calcular baseado no primeiro valor
  const percentageData = hasRealGrowthData 
    ? limitedData.map(item => item.real_growth_percentage || 0)
    : limitedData.map(item => {
        const firstValue = limitedData.length > 0 ? limitedData[0].total_value_eur : 1;
        return ((item.total_value_eur - firstValue) / firstValue) * 100;
      });

  const chartData = {
    labels: limitedData.map(item => new Date(item.date).toLocaleDateString('pt-PT')),
    datasets: [
      {
        label: hasRealGrowthData ? 'Crescimento Real do Portefólio (%)' : 'Variação do Portefólio (%)',
        data: percentageData,
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        borderWidth: 2,
        fill: true,
        tension: 0.4,
        pointBackgroundColor: 'rgb(59, 130, 246)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top' as const,
      },
      title: {
        display: false,
      },
      tooltip: {
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: function(context: any) {
            const dataPoint = limitedData[context.dataIndex];
            const originalValue = dataPoint.total_value_eur;
            
            if (shouldHideValues) {
              const lines = [
                `Variação: ${context.parsed.y.toFixed(2)}%`,
                `Valor: ***`
              ];
              if (hasRealGrowthData) {
                lines.push(`Lucro/Prejuízo: ***`);
              }
              return lines;
            }
            
            if (hasRealGrowthData && dataPoint.real_growth_eur !== undefined) {
              const growthSign = dataPoint.real_growth_eur >= 0 ? '+' : '';
              return [
                `Crescimento Real: ${context.parsed.y.toFixed(2)}%`,
                `Valor: €${originalValue.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}`,
                `Lucro/Prejuízo: ${growthSign}€${dataPoint.real_growth_eur.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}`
              ];
            } else {
              return [
                `Variação: ${context.parsed.y.toFixed(2)}%`,
                `Valor: €${originalValue.toLocaleString('pt-PT', { minimumFractionDigits: 2 })}`
              ];
            }
          }
        }
      }
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Data'
        },
        grid: {
          display: false
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Variação (%)'
        },
        beginAtZero: false,
        ticks: {
          callback: function(value: any) {
            return value.toFixed(1) + '%';
          }
        }
      }
    },
    interaction: {
      mode: 'nearest' as const,
      axis: 'x' as const,
      intersect: false
    }
  };

  return (
    <div>
      <div className="mb-4">
        <TimeFilter 
          selectedFilter={selectedFilter} 
          onFilterChange={setSelectedFilter}
          className="justify-center"
        />
      </div>
      <div className="h-80">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default PerformanceChart;
